<template>
  <div class="events-contacts-browser">
    <h5 v-if="jobTitle">{{ jobTitle }}</h5>
    <contacts-filter id="candidates-results" layout="page" show-filters-on-load :results="contacts"
      :where="filterContactsWhere" @whereUpdated="filterContactsWhereUpdated"
      @pageUpdated="filterContactsPageUpdated" :order="filterContactsOrder"
      :orderOptions="filterContactsOrderOptions" @orderUpdated="filterContactsOrderUpdated"
      :loading="$apollo.queries.contacts.loading || !contacts">
        <!-- <template v-slot:bar v-if="contacts && contacts.totalContacts > 0">
          <b-card body-class="p-3" v-if="job.standardised === null" class="alert-danger my-3">
            <p class="m-0" v-if="isRecruiter">🔗 <strong>Matches are still processing</strong>, in the meantime you can <b>search candidates</b></p>
            <p class="m-0" v-else>🔗 <strong>Matches are still processing</strong>, in the meantime you can <b>search, share or sync your contacts</b></p>
          </b-card>
          <b-card body-class="p-3" v-else-if="hasNoMatches" class="alert-warning my-3">
            <p class="m-0" v-if="$root.isCommunitySite">💪 No matches found, but you can still share the role or upload a CV</p>
            <p class="m-0" v-else>💪 No matches found, but you can still browse your contacts</p>
          </b-card>
        </template> -->

        <template v-slot:contact-card-actions="{ contact }">
          <template v-if="isRecruiter">
            <b-button class="d-none d-md-block mb-2" variant="primary" @click.prevent="() => basketSelected({ contact })" data-tour="select-candidate">
              <template v-if="selectedContactIDs.includes(contact.id)">{{ $t('contacts.ctas.deselect-candidate') }}</template>
              <template v-else>{{ $t('contacts.ctas.select-candidate') }}</template>
            </b-button>
            <b-button class="d-block d-md-none mb-2" variant="primary" @click.prevent="() => selectOneContact({ contact })" data-tour="refer-candidate">
              {{ $t('contacts.ctas.refer-candidate') }}
            </b-button>
          </template>
          <b-button v-else class="mb-2" variant="primary" @click.prevent="() => selectOneContact({ contact })" data-tour="refer-candidate">
            {{ $t('contacts.ctas.refer-candidate') }}
          </b-button>
        </template>

        <template v-slot:no-results>
          <b-card class="my-3 my-md-2 py-3 px-5 h-100 filter-row no-results" body-class="p-0 text-center" v-if="contacts && contacts.totalContacts > 0">
            <div class="question-circle bg-primary-a2 mb-4"><span class="primary-color">?</span></div>
            <h3>{{ $t('contacts.errors.no-search-results-title') }}</h3>
            <template v-if="isRecruiter">
              <p>{{ $t('contacts.errors.no-search-results-description') }}</p>
            </template>
            <template v-else>
              <h4 class="mt-4">{{ $t('contacts.errors.no-search-results-still-share') }}</h4>
              <share centered :url="shareUrl" :providers="canShareJobsWith"
                :preview-text="jobShareText" :preview-args="{ jobTitle }"
                @track="trackShareEvent"/>
            </template>
          </b-card>
          <b-card class="my-2 py-3 px-5 h-100 filter-row" body-class="p-0 text-center" v-else-if="linkedInSearchURL || defaultLinkedInSearchURL">
            <linkedin-search-details v-bind="{ jobID, jobTitle, shareUrl }"
              :linkedInSearchURL="linkedInSearchURL || defaultLinkedInSearchURL"
              @trackedLiBrowseEvent="() => $emit('trackedLiBrowseEvent')"
              @trackShareEvent="({ channel }) => $emit('trackShareEvent', { channel })" />
          </b-card>
        </template>

        <template v-slot:under-filters v-if="isRecruiter">
          <contacts-basket class="d-none d-md-block my-2"
            :selectedContactIDs="selectedContactIDs" :selectedContactData="selectedContactData"
            @clearContacts="basketClear" @removeContact="basketRemove"
            @submit="() => $refs.basketModal.show()"/>
        </template>
    </contacts-filter>
    <template v-if="contacts && contacts.totalContacts > 0">
      <hr-ask-for-introductions-modal v-if="isRecruiter" :jobID="jobID" ref="basketModal"
        :selectedContactIDs="selectedContactIDs" @clearBasket="basketClear"/>
      <employee-makes-referral-modal v-else ref="basketModal" :eventID="eventID" :jobID="jobID"
        :selectedContactIDs="selectedContactIDs" @clearBasket="basketClear"
        @trackShareClick="() => $emit('trackShareClick')"/>
    </template>
  </div>
</template>

<script>
import ms from 'ms';
import qs from 'querystring';
import ContactsFilter from '@/components/contacts-filter/container';
import ContactsBasket from '@/components/contacts/basket';
import JobShowContactRecruitersQuery from '@/graphql/queries/JobShowContactRecruiters.gql';
import JobShowContactEmployeesQuery from '@/graphql/queries/JobShowContactEmployees.gql';
import LinkedinSearchDetails from '@src/components/jobs/LinkedInSearch';
import Share from '@/components/Share';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    eventID: {
      type: String,
      required: true,
    },
    jobID: {
      type: String,
      required: true,
    },
    jobTitle: {
      type: String,
    },
    shareUrl: {
      type: String,
    },
    linkedInSearchURL: {
      type: String,
    },
    jobShareText: {
      type: String,
    },
    canShareJobsWith: {
      default: () => null
    }
  },
  components: {
    ContactsFilter,
    ContactsBasket,
    Share,
    LinkedinSearchDetails,
    HrAskForIntroductionsModal: () => import('@/modals/hr-ask-for-introductions'),
    EmployeeMakesReferralModal: () => import('@/modals/employee-makes-referral'),
  },
  data: () => ({
    filterContactsPage: 1,
    filterContactsLimit: 18,
    filterContactsWhere: {
      // relevance_gte: 30,
      unsuitable: false,
    },
    filterContactsOrder: 'RELEVANCE_DESC',
    filterContactsOrderOptions: {
      'CREATED_DESC': 'Newest first',
      'CREATED_ASC': 'Oldest first',
      // 'UPDATED_DESC': 'Recently updated',
      // 'UPDATED_ASC': 'Least updated',
      'RELEVANCE_DESC': 'Best match',
      'RELEVANCE_ASC': 'Least matched',
    },
    selectedContactIDs: [],
    selectedContactData: {},
  }),
  apollo: {
    contacts: {
      query() {
        return this.isRecruiter ? JobShowContactRecruitersQuery : JobShowContactEmployeesQuery;
      },
      variables() {
        const { jobID, filterContactsPage, filterContactsLimit, filterContactsWhere, filterContactsOrder } = this;
        const { contact } = filterContactsWhere || {};
        return {
          jobID,
          filterContactsPage,
          filterContactsLimit,
          filterContactsWhere,
          filterContactsOrder,
          filterContactsMatchedWhere: contact,
        };
      },
      update(data) {
        if (this.isDemoSite && this.jobID === 'RlYEa') {
          const { filterContactsPage, filterContactsLimit } = this;
          return {
            edges: [],
            page: filterContactsPage,
            limit: filterContactsLimit,
            total: 0,
            totalContacts: 0,
            totalMatches: 0,
            totalPages: 1,
          };
        } else {
          const { job, totalContacts } = (this.isRecruiter ? data.company : data.user) || {};
          const { contactConnections = {}, matchesTotal = {} } = job || {};
          // Set the total
          contactConnections.totalContacts = totalContacts ? totalContacts.total : 0;
          contactConnections.totalMatches = matchesTotal ? matchesTotal.total : 0;
          return contactConnections;
        }
      },
      error(err) {
        this.$notifyErr(err, { prepend: 'Failed to load contacts for job:' });
      },
      debounce: ms('1s'),
      deep: true,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'isDemoSite', 'isRecruiter' ]),
    defaultLinkedInSearchURL() {
      if (this.jobTitle) {
        return `https://www.linkedin.com/search/results/people/?${qs.stringify({
          keywords: this.jobTitle,
          network: [ 'F' ],
          origin: 'GLOBAL_SEARCH_HEADER',
        })}`;
      } else {
        return null;
      }
    },
  },
  methods: {
    filterContactsPageUpdated(newPage) {
      this.filterContactsPage = newPage;
      this.$scrollTo('.contacts-filter-container');
    },
    filterContactsWhereUpdated(newWhere) {
      this.filterContactsWhere = newWhere;
      this.filterContactsPage = 1;
    },
    filterContactsOrderUpdated(newOrder) {
      this.filterContactsOrder = newOrder;
      this.filterContactsPage = 1;
    },
    basketSelected({ contact }) {
      const { id } = contact;
      if (this.selectedContactIDs.includes(id)) {
        this.selectedContactIDs.splice(this.selectedContactIDs.indexOf(id), 1);
        delete this.selectedContactData[id];
      } else {
        this.selectedContactIDs.push(id);
        this.selectedContactData[id] = contact;
      }
    },
    basketRemove(id) {
      if (this.selectedContactIDs.includes(id)) {
        this.selectedContactIDs.splice(this.selectedContactIDs.indexOf(id), 1);
        delete this.selectedContactData[id];
      }
    },
    basketClear() {
      this.selectedContactIDs = [];
      this.selectedContactData = {};
    },
    selectOneContact({ contact }) {
      const { id } = contact;
      this.selectedContactIDs = [ id ];
      this.selectedContactData = { [id]: contact };
      this.$nextTick(() => this.$refs.basketModal.show());
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/modals/event-contacts-browser.vue